import { IUserInfo, IAddressSG, IAddressOther } from "../types/appDataTypes"
import { TSteps } from "../reducers/appReducers"
import { getState, dispatch } from "../store"
import moment from "moment"
import axios from "axios"
import { appActions, userInfoActions } from "../actions"
import { TLanguage } from "../reducers/configReducers"

/**
 * Class names
 * @param classes object
 * @returns class list string
 *
 * Use class names as object key, boolean statement as value.
 * Will return class names which value is true.
 */
export function classNames(classes: { [key: string]: boolean }): string {
    return Object.entries(classes)
        .filter(([, value]) => value)
        .map(([key]) => key)
        .join(" ")
}

export const noSymbolFilter = (value: string): string => value.replace(/[\d'"\-/^`~!@#$%^&*()_+={}[\]|\\:;“’<,>.?๐฿]/ig, "")

export const digitsOnlyFilter = (value: string): string => value.replace(/[\D]/ig, "")

export const digitsAndDecimalOnlyFilter = (value: string): string => {
    const filter = /[\d.]+/ig.exec(value)
    return filter ? filter[0] : ""
}

export const digitsOnlyRestrict = (value: string): string => {
    const filter = /[\d]+/ig.exec(value)
    return filter ? filter[0] : ""
}

export const resizePrimaryImage = (image: HTMLImageElement, canvas: HTMLCanvasElement, rotation: number): HTMLCanvasElement => {
    if (!canvas) return null
    const MAX_SIZE = 6000
    const { naturalHeight, naturalWidth } = image

    if (rotation && ([90, 270, -90, -270].indexOf(rotation) > -1)) {
        canvas.width = naturalHeight
        canvas.height = naturalWidth

        if (naturalWidth >= naturalHeight && naturalWidth > MAX_SIZE) {
            canvas.width = MAX_SIZE
            canvas.height = Math.round(naturalHeight * MAX_SIZE / naturalWidth)
        }

        if (naturalWidth < naturalHeight && naturalHeight > MAX_SIZE) {
            canvas.height = MAX_SIZE
            canvas.width = Math.round(naturalWidth * MAX_SIZE / naturalHeight)
        }

    } else {
        canvas.width = naturalWidth
        canvas.height = naturalHeight

        if (naturalHeight >= naturalWidth && naturalHeight > MAX_SIZE) {
            canvas.height = MAX_SIZE
            canvas.width = Math.round(naturalWidth * MAX_SIZE / naturalHeight)
        }

        if (naturalHeight < naturalWidth && naturalWidth > MAX_SIZE) {
            canvas.width = MAX_SIZE
            canvas.height = Math.round(naturalHeight * MAX_SIZE / naturalWidth)
        }
    }

    const ctx = canvas.getContext("2d")
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.save()
    ctx.translate(canvas.width / 2, canvas.height / 2)
    ctx.rotate(rotation * (Math.PI / 180))
    ctx.drawImage(image, -(naturalWidth / 2), -(naturalHeight / 2))
    ctx.restore()

    return canvas
}

export const resizeAvatarImage = (image: HTMLCanvasElement, canvas: HTMLCanvasElement): HTMLCanvasElement => {
    if (!canvas) return image

    const MAX_SIZE = 6000

    canvas.width = image.width
    canvas.height = image.height

    if (image.width >= image.height) {
        if (image.width > MAX_SIZE) {
            canvas.height = image.height / image.width * MAX_SIZE
            canvas.width = MAX_SIZE
        }
    }

    if (image.width < image.height) {
        if (image.height > MAX_SIZE) {
            canvas.width = image.width / image.height * MAX_SIZE
            canvas.height = MAX_SIZE
        }
    }

    const ctx = canvas.getContext("2d")
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height)

    return canvas
}

export const checkStep = (data: IUserInfo): TSteps => {
    let step: TSteps = "BasicInfo";
    const activeCountry =
        data.activeCountry.toUpperCase() as IUserInfo['activeCountry'] || "SG"

    let minAge
    let isJunior = false

    try {
        minAge = getState().config.appConfig.countryConfig[activeCountry.toUpperCase()].minAge || 18
    } catch { minAge = 18 }

    if (
        !!data.fullName
        && !!data.dateOfBirth
        && !!data.id
        && (activeCountry !== "TH" || !!data.gender)
        && !!data.citizenship
        && !!data.mobile
        && !!data.email
    ) {
        isJunior = data["dateOfBirth"] && moment(data["dateOfBirth"]).isAfter(moment().subtract(minAge, "year"));
        step = isJunior ? "ParentInfo" : "AddressInfo"
    } else { return step }

    if (isJunior) {
        if (
            !!data.parentInfo
            && !!data.parentInfo.id
            && !!data.parentInfo.name
        ) {
            step = "AddressInfo"
        } else { return step }
    }

    if (
        !!data.addressInfo
        && (
            (!!(data.addressInfo as IAddressSG).street && !!(data.addressInfo as IAddressSG).block)
            || (!!(data.addressInfo as IAddressOther).line1)
        )
    ) {
        step = "WorkStatus"
    } else { return step }

    if (!!data.employmentStatus) {
        step = "ArtistStatus"
    } else { return step }

    if (!!data.artistInfo && !!data.artistInfo.artistStatus) {
        step = "Dashboard"
    } else { return step }

    return step;
}

export const isDesktopWidth = () => {
    return window.innerWidth >= 700
}

export const retrieveLangPack = (lang: TLanguage, country?: IUserInfo["activeCountry"]) => {
    let langPackUrl = `data/lang-${country}-${lang}.json`

    return axios.get(langPackUrl).then(response => {
        if (response && response.status === 200 && response.data) {
            dispatch(appActions.updateLanguage(lang, response.data))
            dispatch(userInfoActions.updateUserInfo({ "lang": lang }))
        }
    })
}

export const retrieveTNCData = (country?: IUserInfo["activeCountry"]) => {
    const tncUrl = `data/tnc-${country.toLocaleUpperCase()}.json`;
    return axios.get(tncUrl).then(response => {
        if (response && response.status === 200 && response.data) {
            dispatch(appActions.updateTNCData(response.data))
        }
    })
}